import React, { useState } from 'react';

const TABS = [
  { id: 'revenue-analysis', label: 'Revenue Analysis', icon: '📊' },
  { id: 'revenue-forecast', label: 'Revenue Forecast', icon: '🔮' },
  { id: 'practice-intelligence', label: 'Practice Intelligence', icon: '💡' },
];

const REVENUE_PAGES = [
  { id: 'summary', title: 'Revenue Summary', subtitle: 'Total, average, and key metrics', icon: '📊', borderColor: 'border-blue-500', bgColor: 'bg-blue-50', hoverColor: 'hover:bg-blue-100' },
  { id: 'monthly-breakdown', title: 'Monthly Breakdown', subtitle: 'All months with income detail', icon: '📅', borderColor: 'border-indigo-500', bgColor: 'bg-indigo-50', hoverColor: 'hover:bg-indigo-100' },
  { id: 'peak-month', title: 'Peak Month', subtitle: 'Highest revenue month analysis', icon: '🏆', borderColor: 'border-green-500', bgColor: 'bg-green-50', hoverColor: 'hover:bg-green-100' },
  { id: 'low-month', title: 'Low Month', subtitle: 'Lowest revenue month analysis', icon: '📉', borderColor: 'border-red-500', bgColor: 'bg-red-50', hoverColor: 'hover:bg-red-100' },
];

const FORECAST_PAGES = [
  { id: 'trend', title: 'Trend Direction', subtitle: 'Revenue trajectory analysis', icon: '📈', borderColor: 'border-orange-500', bgColor: 'bg-orange-50', hoverColor: 'hover:bg-orange-100' },
  { id: 'projections', title: 'Monthly Projections', subtitle: '6-month revenue forecast', icon: '🔮', borderColor: 'border-purple-500', bgColor: 'bg-purple-50', hoverColor: 'hover:bg-purple-100' },
  { id: 'confidence', title: 'Forecast Confidence', subtitle: 'Model accuracy and reliability', icon: '🎯', borderColor: 'border-blue-500', bgColor: 'bg-blue-50', hoverColor: 'hover:bg-blue-100' },
];

const INTEL_PAGES = [
  { id: 'stability', title: 'Revenue Stability', subtitle: 'Variance and consistency analysis', icon: '⚖️', borderColor: 'border-teal-500', bgColor: 'bg-teal-50', hoverColor: 'hover:bg-teal-100' },
  { id: 'growth', title: 'Growth Opportunities', subtitle: 'Data-driven insights for your practice', icon: '🌱', borderColor: 'border-green-500', bgColor: 'bg-green-50', hoverColor: 'hover:bg-green-100' },
];

const fmt = (val) =>
  val != null
    ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : 'N/A';

const IncomeByMonthAnalytics = ({ chartData }) => {
  const [activeTab, setActiveTab] = useState('revenue-analysis');
  const [subPage, setSubPage] = useState(null);

  if (!chartData) return null;

  const {
    chartItems, forecastItems, forecastStats,
    totalRevenue, avgMonthly, variancePct,
    highestMonth, lowestMonth, stabilityRating, monthCount,
  } = chartData;

  const BackButton = ({ onClick }) => (
    <button
      onClick={onClick}
      className="flex items-center text-gray-600 hover:text-gray-900 mb-5 transition-colors text-sm font-medium"
    >
      <span className="mr-2">&larr;</span> Back
    </button>
  );

  const NavCard = ({ item, onClick }) => (
    <button
      onClick={onClick}
      className={`w-full text-left border-l-4 ${item.borderColor} ${item.bgColor} ${item.hoverColor} rounded-lg px-4 py-3 transition-all flex items-center justify-between group`}
    >
      <div className="flex items-center gap-3">
        <span className="text-xl">{item.icon}</span>
        <div>
          <h3 className="text-sm font-bold text-gray-900">{item.title}</h3>
          <p className="text-xs text-gray-500">{item.subtitle}</p>
        </div>
      </div>
      <span className="text-gray-400 group-hover:text-gray-600">&rarr;</span>
    </button>
  );

  // ===== REVENUE ANALYSIS SUB-PAGES =====
  const renderRevenuePage = () => {
    switch (subPage) {
      case 'summary':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Revenue Summary</h2>
            <p className="text-sm text-gray-500 mb-5">{monthCount} months of data</p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[
                { label: 'Total Revenue', value: fmt(totalRevenue), color: 'bg-blue-50 border-blue-200' },
                { label: 'Avg Monthly', value: fmt(avgMonthly), color: 'bg-green-50 border-green-200' },
                { label: 'Month-to-Month Variance', value: `${variancePct.toFixed(1)}%`, color: 'bg-purple-50 border-purple-200' },
                { label: 'Revenue Stability', value: stabilityRating, color: 'bg-teal-50 border-teal-200' },
              ].map((item, i) => (
                <div key={i} className={`rounded-lg p-4 border ${item.color}`}>
                  <p className="text-xs text-gray-500 font-semibold uppercase mb-1">{item.label}</p>
                  <p className="text-xl font-bold text-gray-900">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-700 space-y-2">
              <p><strong>Data range:</strong> {chartItems[0].label} — {chartItems[chartItems.length - 1].label}</p>
              <p><strong>Peak month:</strong> {highestMonth.label} ({fmt(highestMonth.income)})</p>
              <p><strong>Lowest month:</strong> {lowestMonth.label} ({fmt(lowestMonth.income)})</p>
              <p><strong>Revenue range:</strong> {fmt(highestMonth.income - lowestMonth.income)}{lowestMonth.income > 0 ? ` (${(((highestMonth.income - lowestMonth.income) / lowestMonth.income) * 100).toFixed(1)}% spread)` : ''}</p>
            </div>
          </div>
        );

      case 'monthly-breakdown':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Monthly Breakdown</h2>
            <p className="text-sm text-gray-500 mb-5">All {monthCount} months, most recent first</p>
            <div className="space-y-2">
              {[...chartItems].reverse().map((item, i) => {
                const aboveAvg = item.income >= avgMonthly;
                const pctVsAvg = avgMonthly > 0
                  ? (((item.income - avgMonthly) / avgMonthly) * 100).toFixed(1)
                  : '0.0';
                const isPeak = item.month === highestMonth.month;
                const isLow = item.month === lowestMonth.month;
                return (
                  <div
                    key={i}
                    className={`rounded-lg p-3 flex items-center justify-between ${
                      isPeak ? 'bg-green-50 border border-green-200'
                      : isLow ? 'bg-red-50 border border-red-100'
                      : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-900">{item.label}</span>
                      {isPeak && <span className="text-xs text-green-600 font-semibold bg-green-100 px-2 py-0.5 rounded-full">Peak</span>}
                      {isLow && <span className="text-xs text-red-600 font-semibold bg-red-100 px-2 py-0.5 rounded-full">Lowest</span>}
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-sm text-gray-900">{fmt(item.income)}</span>
                      <span className={`ml-2 text-xs ${aboveAvg ? 'text-green-600' : 'text-red-500'}`}>
                        {aboveAvg ? '+' : ''}{pctVsAvg}% vs avg
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'peak-month':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Peak Month</h2>
            <p className="text-sm text-gray-500 mb-5">Highest revenue month in your data</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-5">
              <p className="text-base font-bold text-green-800">{highestMonth.label}</p>
              <p className="text-2xl font-bold text-green-700">{fmt(highestMonth.income)}</p>
              {avgMonthly > 0 && (
                <p className="text-sm text-green-600 mt-1">
                  {fmt(highestMonth.income - avgMonthly)} above average ({(((highestMonth.income - avgMonthly) / avgMonthly) * 100).toFixed(1)}%)
                </p>
              )}
            </div>
            <div className="text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-base mb-2">What this month can tell you:</p>
                <ul className="list-disc ml-5 space-y-2">
                  <li>Review what drove volume in {highestMonth.label} — referral sources, scheduling capacity, client mix</li>
                  <li>Identify whether this peak recurs seasonally or was a one-time event</li>
                  <li>Use this month's schedule and capacity as the target for your operating rhythm</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'low-month':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Low Month</h2>
            <p className="text-sm text-gray-500 mb-5">Lowest revenue month in your data</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
              <p className="text-base font-bold text-red-800">{lowestMonth.label}</p>
              <p className="text-2xl font-bold text-red-700">{fmt(lowestMonth.income)}</p>
              {avgMonthly > 0 && (
                <p className="text-sm text-red-600 mt-1">
                  {fmt(avgMonthly - lowestMonth.income)} below average ({(((avgMonthly - lowestMonth.income) / avgMonthly) * 100).toFixed(1)}%)
                </p>
              )}
            </div>
            <div className="text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-base mb-2">What to look at:</p>
                <ul className="list-disc ml-5 space-y-2">
                  <li>Review cancellations and no-shows during {lowestMonth.label} — cross-reference with your No-Show Analysis</li>
                  <li>Check whether this was a holiday month, vacation period, or a billing/collection issue</li>
                  <li>Consider proactive client outreach ahead of historically low months</li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ===== FORECAST SUB-PAGES =====
  const renderForecastPage = () => {
    if (!forecastStats || forecastItems.length === 0) return null;

    const trendColor =
      forecastStats.trendDirection === 'increasing' ? '#16a34a'
      : forecastStats.trendDirection === 'decreasing' ? '#dc2626'
      : '#6b7280';

    switch (subPage) {
      case 'trend':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Trend Direction</h2>
            <p className="text-sm text-gray-500 mb-5">Based on your historical revenue pattern</p>
            <div
              className="rounded-lg p-5 mb-5 text-center border"
              style={{
                backgroundColor: forecastStats.trendDirection === 'increasing' ? '#f0fdf4'
                  : forecastStats.trendDirection === 'decreasing' ? '#fef2f2'
                  : '#f9fafb',
                borderColor: forecastStats.trendDirection === 'increasing' ? '#bbf7d0'
                  : forecastStats.trendDirection === 'decreasing' ? '#fecaca'
                  : '#e5e7eb',
              }}
            >
              <p className="text-3xl font-bold mb-1" style={{ color: trendColor }}>
                {forecastStats.trendDirection === 'increasing' ? '↑'
                  : forecastStats.trendDirection === 'decreasing' ? '↓'
                  : '→'}{' '}
                {forecastStats.trendDirection.charAt(0).toUpperCase() + forecastStats.trendDirection.slice(1)}
              </p>
              <p className="text-sm text-gray-600">
                {forecastStats.trendPct.toFixed(1)}% projected change over forecast period
              </p>
            </div>
            <div className="text-sm text-gray-700 space-y-3">
              {forecastStats.trendDirection === 'increasing' && (
                <>
                  <p>Your revenue trend is positive. The model projects continued growth over the next 6 months.</p>
                  <ul className="list-disc ml-5 space-y-2">
                    <li>Review your capacity — a growing practice may need to expand scheduling bandwidth</li>
                    <li>Consider whether referral pipelines can sustain this trajectory</li>
                    <li>Plan for higher-demand months using your peak-month patterns</li>
                  </ul>
                </>
              )}
              {forecastStats.trendDirection === 'decreasing' && (
                <>
                  <p>Your revenue trend shows a declining pattern. The model projects continued softening.</p>
                  <ul className="list-disc ml-5 space-y-2">
                    <li>Cross-reference with your No-Show Analysis to see if attendance is driving the decline</li>
                    <li>Assess whether client discharge rates exceed new client intake</li>
                    <li>Consider referral outreach to supplement the pipeline</li>
                  </ul>
                </>
              )}
              {forecastStats.trendDirection === 'stable' && (
                <>
                  <p>Your revenue is tracking steadily. The model projects continued consistency.</p>
                  <ul className="list-disc ml-5 space-y-2">
                    <li>Stable revenue is a strong foundation — identify what's working and protect it</li>
                    <li>Evaluate whether there are opportunities to grow while maintaining consistency</li>
                    <li>Review your variance rating alongside stability to assess overall risk</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        );

      case 'projections':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Monthly Projections</h2>
            <p className="text-sm text-gray-500 mb-5">{forecastStats.forecastPeriod}</p>
            <div className="space-y-2 mb-5">
              {forecastItems.map((item, i) => (
                <div key={i} className="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3 flex justify-between items-center">
                  <span className="font-semibold text-sm text-gray-900">{item.label}</span>
                  <span className="font-bold text-orange-700">{fmt(item.income)}</span>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-1">
              <p className="text-sm text-gray-700">
                <strong>Projected average:</strong> {fmt(forecastStats.projectedAvg)}/month
              </p>
              <p className="text-xs text-gray-500">
                Forecasts are based on linear trend analysis of your historical data and are for planning purposes only.
              </p>
            </div>
          </div>
        );

      case 'confidence':
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Forecast Confidence</h2>
            <p className="text-sm text-gray-500 mb-5">How well the model fits your historical data</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
              <p className="text-xs text-gray-500 font-semibold mb-2">MODEL CONFIDENCE (R²)</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-200 h-3 rounded-full">
                  <div className="h-3 rounded-full bg-blue-500" style={{ width: `${forecastStats.confidence}%` }} />
                </div>
                <span className="font-bold text-blue-700">{forecastStats.confidence}%</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {forecastStats.confidence >= 70
                  ? 'Good fit — your revenue follows a consistent trend pattern.'
                  : forecastStats.confidence >= 40
                  ? 'Moderate fit — some variability, but a trend is present.'
                  : 'Low fit — revenue is highly variable. Treat forecasts as directional only.'}
              </p>
            </div>
            <div className="text-sm text-gray-700 space-y-2">
              <p>The confidence score (R²) reflects how closely a linear trend fits your historical data. Higher is more reliable.</p>
              <p><strong>More data improves confidence.</strong> You currently have {monthCount} months recorded. Adding more months sharpens the forecast.</p>
              <p className="text-xs text-gray-500 mt-3">
                Forecasts use linear regression and are intended for practice planning only, not financial guarantees.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ===== PRACTICE INTELLIGENCE SUB-PAGES =====
  const renderIntelPage = () => {
    switch (subPage) {
      case 'stability': {
        const stabilityColor =
          stabilityRating === 'High' ? '#16a34a'
          : stabilityRating === 'Moderate' ? '#d97706'
          : '#dc2626';
        const stabilityBg =
          stabilityRating === 'High' ? 'bg-green-50 border-green-200'
          : stabilityRating === 'Moderate' ? 'bg-yellow-50 border-yellow-200'
          : 'bg-red-50 border-red-200';
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Revenue Stability</h2>
            <p className="text-sm text-gray-500 mb-5">Month-to-month variance analysis</p>
            <div className={`rounded-lg p-4 border mb-5 ${stabilityBg}`}>
              <p className="text-xs text-gray-500 font-semibold mb-1">STABILITY RATING</p>
              <p className="text-2xl font-bold" style={{ color: stabilityColor }}>{stabilityRating}</p>
              <p className="text-sm text-gray-600 mt-1">Month-to-month variance: {variancePct.toFixed(1)}%</p>
            </div>
            <div className="text-sm text-gray-700 space-y-3">
              <p><strong>Variance &lt;15% — High stability.</strong> Revenue is consistent month-to-month. Low financial risk.</p>
              <p><strong>Variance 15–30% — Moderate.</strong> Some seasonal or situational variation present.</p>
              <p><strong>Variance &gt;30% — Variable.</strong> Revenue fluctuates significantly. Consider strategies to smooth income.</p>
              <p className="mt-3">
                Your practice is currently at <strong>{variancePct.toFixed(1)}%</strong> variance —{' '}
                <strong style={{ color: stabilityColor }}>{stabilityRating}</strong> stability.
              </p>
            </div>
          </div>
        );
      }

      case 'growth': {
        const revenueGap = highestMonth.income - avgMonthly;
        const annualizedGap = revenueGap * 12;
        return (
          <div>
            <BackButton onClick={() => setSubPage(null)} />
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Growth Opportunities</h2>
            <p className="text-sm text-gray-500 mb-5">Data-derived insights from your revenue patterns</p>
            <div className="space-y-4 text-sm text-gray-700">
              <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                <p className="font-semibold text-green-800 mb-1">Revenue Gap to Peak</p>
                <p>
                  If every month matched your peak ({highestMonth.label}), you would generate an additional{' '}
                  <strong className="text-green-700">{fmt(annualizedGap)}/year</strong> above your current run rate.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <p className="font-semibold text-blue-800 mb-1">Low-Month Recovery</p>
                <p>
                  Raising your lowest month ({lowestMonth.label}) to your monthly average would add approximately{' '}
                  <strong className="text-blue-700">{fmt(avgMonthly - lowestMonth.income)}</strong> in that month alone.
                </p>
              </div>
              {forecastStats && (
                <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
                  <p className="font-semibold text-orange-800 mb-1">Forecast vs Historical</p>
                  <p>
                    Your 6-month projected average is <strong className="text-orange-700">{fmt(forecastStats.projectedAvg)}/month</strong>.{' '}
                    {forecastStats.projectedAvg > avgMonthly
                      ? `That's ${fmt(forecastStats.projectedAvg - avgMonthly)} above your historical average — your trend is working.`
                      : `That's ${fmt(avgMonthly - forecastStats.projectedAvg)} below your historical average — worth monitoring closely.`}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const getSubPageContent = () => {
    if (activeTab === 'revenue-analysis') return renderRevenuePage();
    if (activeTab === 'revenue-forecast') return renderForecastPage();
    if (activeTab === 'practice-intelligence') return renderIntelPage();
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-3">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSubPage(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {subPage ? (
        getSubPageContent()
      ) : (
        <>
          {/* Revenue Analysis */}
          {activeTab === 'revenue-analysis' && (
            <>
              <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Practice Revenue Analysis</h2>
              <p className="text-sm text-gray-500 text-center mb-4">Select a category to explore your revenue data</p>
              <div className="grid grid-cols-2 gap-3">
                {REVENUE_PAGES.map(item => <NavCard key={item.id} item={item} onClick={() => setSubPage(item.id)} />)}
              </div>
            </>
          )}

          {/* Revenue Forecast */}
          {activeTab === 'revenue-forecast' && (
            <>
              <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Revenue Forecast</h2>
              {!forecastStats || forecastItems.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-5xl mb-4">🔮</p>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">4 months needed to unlock forecasting</h3>
                  <p className="text-sm text-gray-500">
                    You have {monthCount} month{monthCount !== 1 ? 's' : ''} of data.
                    Add {Math.max(0, 4 - monthCount)} more to unlock AI revenue forecasting.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500 text-center mb-4">Select a category to explore forecast insights</p>
                  <div className="grid grid-cols-2 gap-3">
                    {FORECAST_PAGES.map(item => <NavCard key={item.id} item={item} onClick={() => setSubPage(item.id)} />)}
                  </div>
                </>
              )}
            </>
          )}

          {/* Practice Intelligence */}
          {activeTab === 'practice-intelligence' && (
            <>
              <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Practice Intelligence</h2>
              <p className="text-sm text-gray-500 text-center mb-4">Data-driven insights from your revenue patterns</p>
              <div className="grid grid-cols-2 gap-3">
                {INTEL_PAGES.map(item => <NavCard key={item.id} item={item} onClick={() => setSubPage(item.id)} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default IncomeByMonthAnalytics;
